// =============================================
// Image Generator Service — FAL.AI featured images for articles
// Bridges content engine context → FAL.AI → DB
// =============================================

import { supabase } from "@/integrations/supabase/client";
import falaiService from "./falaiService";
import { loadPrompt } from "@/services/llm/promptLoader";
import { getLLMProvider } from "@/services/llm";
import { compilePromptSafe } from "@/services/contentEngine/promptCompiler";

const DEFAULT_MODEL = "fal-ai/flux-pro/v1.1";
const ARTICLE_EXCERPT_CHARS = 1500;

/** Map document_role → visual style hint for image prompt (fallback when LLM fails) */
const ROLE_STYLE_HINTS: Record<string, string> = {
  definition: "conceptual, clean background",
  glossary: "educational, clean composition",
  history: "vintage tones, archival feel",
  how_to: "step-by-step instructional, practical scene",
  protocol: "professional, structured, clinical",
  guide: "friendly tutorial, practical demonstration",
  comparison: "side-by-side contrast, split composition",
  troubleshooting: "problem-solving, diagnostic, close-up detail",
  commercial: "product showcase, lifestyle, aspirational",
};

/**
 * Strip HTML tags + collapse whitespace.
 * Zostawia tylko widoczny tekst — żeby LLM dostał czystą prozę zamiast HTML markup.
 */
function stripHtmlToText(html: string): string {
  if (!html) return "";
  // Browser path: DOMParser (czyste + handle entities)
  if (typeof DOMParser !== "undefined") {
    try {
      const doc = new DOMParser().parseFromString(html, "text/html");
      return (doc.body?.textContent ?? "").replace(/\s+/g, " ").trim();
    } catch {
      /* fallthrough to regex */
    }
  }
  // SSR/Node fallback: regex strip
  return html
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\s+/g, " ")
    .trim();
}

// =============================================
// Build image prompt from article context
// =============================================

/**
 * Fallback prompt (gdy LLM nie odpowie / final_html pusty / loadPrompt zwróci null).
 * Bazuje WYŁĄCZNIE na temacie artykułu (seo_title + canonical_question) — bez encji
 * z mapy (powodowało "sucha sierść" dla wszystkich artykułów w mapie PsyKoty).
 */
function buildFallbackPrompt(opts: {
  seoTitle: string;
  canonicalQuestion: string;
  styleHint: string;
}): string {
  return [
    `A professional editorial photograph for an article titled "${opts.seoTitle}".`,
    `The image MUST visually represent the article topic: ${opts.canonicalQuestion}`,
    `Show the actual subject of the article — literal, concrete, recognizable scene.`,
    `Style: ${opts.styleHint}.`,
    `Photorealistic, clean composition, modern aesthetics, 16:9 aspect ratio, no text overlays, no watermarks, no abstract symbolism.`,
  ].join(" ");
}

/**
 * Build image prompt for FAL.ai by feeding the FULL article (HTML stripped, first
 * ~1500 chars) to an LLM, which writes a 50-80 word visual scene description.
 *
 * Why LLM-based: poprzednie wersje używały tylko seo_title + entity z mapy →
 * FAL nie miał kontekstu wizualnego (nie wiedział że artykuł o "kąpaniu kota"
 * mówi o łazience, mokrej sierści, ręczniku, misce z wodą). LLM czyta artykuł
 * i wyciąga konkretne obiekty/sceny które MAJĄ pojawić się na zdjęciu.
 *
 * Fallback: gdy loadPrompt zwróci null lub LLM padnie → seo_title-based prompt.
 */
export async function buildImagePrompt(
  nodeId: string,
  _mapId: string,
): Promise<string> {
  const { data: node } = await (supabase as any)
    .from("thematic_nodes")
    .select("title, document_role, canonical_question")
    .eq("id", nodeId)
    .single();

  const { data: content } = await (supabase as any)
    .from("thematic_node_content")
    .select("seo_title, final_html")
    .eq("node_id", nodeId)
    .single();

  const seoTitle = content?.seo_title || node?.title || "article";
  const canonicalQuestion = (node?.canonical_question as string) || seoTitle;
  const documentRole = node?.document_role || "definition";
  const styleHint = ROLE_STYLE_HINTS[documentRole] || "professional, clean, modern";
  const finalHtml = (content?.final_html as string) || "";

  const fallback = buildFallbackPrompt({ seoTitle, canonicalQuestion, styleHint });

  // Brak treści artykułu → od razu fallback (np. obraz generowany przed final_html)
  if (!finalHtml.trim()) {
    console.log(`[ImageGen] No final_html for node=${nodeId.slice(0, 8)} — using fallback prompt`);
    return fallback;
  }

  try {
    const articleText = stripHtmlToText(finalHtml).slice(0, ARTICLE_EXCERPT_CHARS);
    if (articleText.length < 100) {
      // Stripped za krótkie → final_html prawdopodobnie pusty/uszkodzony
      console.log(`[ImageGen] Stripped article too short (${articleText.length} chars) — fallback`);
      return fallback;
    }

    const dbPrompt = await loadPrompt("image_prompt_generation" as any);
    if (!dbPrompt) {
      console.log(`[ImageGen] No DB prompt 'image_prompt_generation' — fallback`);
      return fallback;
    }

    const compiled = compilePromptSafe(
      dbPrompt.template,
      { seo_title: seoTitle, article_excerpt: articleText },
      "image_prompt_generation",
      fallback,
    );

    // Jeśli compilePromptSafe zwrócił fallback (unresolved vars) — użyj go
    if (compiled === fallback) {
      console.log(`[ImageGen] Template compile failed — using fallback`);
      return fallback;
    }

    const provider = getLLMProvider(dbPrompt.provider || "openai");
    const response = await provider.call(compiled, {
      model: dbPrompt.modelId || "gpt-5.4-mini",
      temperature: dbPrompt.temperature ?? 0.6,
      step: "image_prompt_generation",
      nodeId,
    });

    const llmPrompt = (response.text || "").trim();
    if (!llmPrompt || llmPrompt.length < 50) {
      console.log(`[ImageGen] LLM returned too short prompt (${llmPrompt.length} chars) — fallback`);
      return fallback;
    }

    console.log(
      `[ImageGen] LLM prompt built for node=${nodeId.slice(0, 8)} ` +
      `(cost=$${(response.costUsd ?? 0).toFixed(4)}, chars=${llmPrompt.length}): "${llmPrompt.slice(0, 120)}..."`
    );
    return llmPrompt;
  } catch (err) {
    console.error(
      `[ImageGen] LLM prompt build failed for node=${nodeId.slice(0, 8)} — fallback:`,
      err instanceof Error ? err.message : err,
    );
    return fallback;
  }
}

// =============================================
// Generate featured image for a single node
// =============================================

export async function generateFeaturedImage(
  nodeId: string,
  mapId: string,
  model?: string,
): Promise<{ url: string; prompt: string } | null> {
  const useModel = model || DEFAULT_MODEL;

  console.log(`[ImageGen] Generating featured image for node=${nodeId.slice(0, 8)}, model=${useModel}`);

  // Client-side apiKey guard usunięty (2026-04-22) — nginx proxy server-side
  // nadpisuje Authorization header z env FAL_AI_API_KEY (patrz nginx.conf:137-148).
  // Klucz NIGDY nie trafia do browser bundle. Worker path (process.env) zachowany
  // jako fallback dla Node runtime bez nginx-a.
  const { env } = await import("@/lib/env");
  const apiKey = env("VITE_FAL_AI_API_KEY") || (typeof process !== "undefined" ? process.env.FAL_AI_API_KEY : undefined);
  if (apiKey) {
    falaiService.setConfig({ apiKey });
  }
  // Gdy apiKey pusty (browser bez VITE_ — normal case w prod Coolify), falaiService
  // wyśle pusty Authorization header, a nginx dokleją prawdziwy przez proxy_set_header.

  // Build prompt from article context
  const prompt = await buildImagePrompt(nodeId, mapId);

  // Call FAL.AI
  const start = Date.now();
  const result = await falaiService.generateImage({
    prompt,
    model: useModel,
    numImages: 1,
  });
  const duration = Date.now() - start;

  if (!result.success || !result.images?.length) {
    console.error(`[ImageGen] FAL.AI failed for node=${nodeId.slice(0, 8)}:`, result.error);
    return null;
  }

  const imageUrl = result.images[0].url;
  console.log(`[ImageGen] Generated: ${imageUrl.slice(0, 80)}... (${duration}ms)`);

  // Save to DB
  const { error } = await (supabase as any)
    .from("thematic_node_content")
    .update({
      featured_image_url: imageUrl,
      image_prompt: prompt,
      image_model: useModel,
    })
    .eq("node_id", nodeId);

  if (error) {
    console.error(`[ImageGen] DB save failed for node=${nodeId.slice(0, 8)}:`, error.message);
    return null;
  }

  console.log(`[ImageGen] Saved featured_image_url for node=${nodeId.slice(0, 8)}`);
  return { url: imageUrl, prompt };
}

// =============================================
// Batch generate images for multiple nodes
// =============================================

export async function generateBatchImages(
  nodeIds: string[],
  mapId: string,
  model?: string,
): Promise<{ success: number; failed: number; skipped: number }> {
  let success = 0;
  let failed = 0;
  let skipped = 0;

  console.log(`[ImageGen] Batch: ${nodeIds.length} nodes, model=${model || DEFAULT_MODEL}`);

  for (const nodeId of nodeIds) {
    // Check if image already exists
    const { data: existing } = await (supabase as any)
      .from("thematic_node_content")
      .select("featured_image_url")
      .eq("node_id", nodeId)
      .single();

    if (existing?.featured_image_url) {
      console.log(`[ImageGen] Skip node=${nodeId.slice(0, 8)} — image already exists`);
      skipped++;
      continue;
    }

    const result = await generateFeaturedImage(nodeId, mapId, model);
    if (result) {
      success++;
    } else {
      failed++;
    }

    // 1s delay between calls to avoid rate limiting
    if (nodeIds.indexOf(nodeId) < nodeIds.length - 1) {
      await new Promise((r) => setTimeout(r, 1000));
    }
  }

  console.log(`[ImageGen] Batch done: ${success} success, ${failed} failed, ${skipped} skipped`);
  return { success, failed, skipped };
}

// =============================================
// Fetch image URL as File (for WP Media upload)
// =============================================

export async function fetchImageAsFile(
  imageUrl: string,
  filename: string,
): Promise<File | null> {
  try {
    const response = await fetch(imageUrl);
    if (!response.ok) {
      console.error(`[ImageGen] Failed to fetch image: ${response.status}`);
      return null;
    }
    const blob = await response.blob();
    const contentType = blob.type || "image/png";
    const ext = contentType.includes("jpeg") ? ".jpg" : ".png";
    const finalFilename = filename.endsWith(ext) ? filename : `${filename}${ext}`;
    return new File([blob], finalFilename, { type: contentType });
  } catch (err) {
    console.error(`[ImageGen] Error fetching image as file:`, err instanceof Error ? err.message : err);
    return null;
  }
}
